'use client';
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    id: 'faq-arch',
    q: 'Which applications do you support?',
    a: 'Applications built on Clean Architecture with the Repository Pattern: an MVVM (or equivalent) presentation layer, a repository layer between the app and its data sources, and an API and ORM behind it. Offline-first applications, where a local database syncs with the API in the background, are welcome. You confirm this when you submit a request, and an architect verifies it in a review before any work starts.',
  },
  {
    id: 'faq-pay',
    q: 'What am I paying for?',
    a: 'For keeping your architecture clean while your application is fixed, extended and maintained — including changes produced with AI. AI makes code cheap to write; we make sure what it writes lands in the right layer, respects your repositories and view models, and passes review before it reaches your codebase.',
  },
  {
    id: 'faq-units',
    q: 'How do you work out what my request will cost?',
    a: 'After evaluating your request we recommend the probable units of each AI tool required (for example seat-months of an AI coding assistant or API credits), together with the review and engineering effort, and give you a cost breakdown line by line. The recommended total is guidance only — you decide how much to reserve.',
  },
  {
    id: 'faq-not-clean',
    q: "What if my application doesn't follow these principles yet?",
    a: 'Tell us in the request notes. We only take on ongoing support for applications already built this way, but we can scope a refactor toward Clean Architecture as a separate piece of work, or build a new AI tool on the same foundations from the start.',
  },
  {
    id: 'faq-1',
    q: 'Is the recommended reservation amount mandatory?',
    a: 'No. The recommended reservation is provided as guidance only, based on the estimated units, tools and effort your request may require. You decide how much you are comfortable reserving. You can reserve the full amount, a partial amount, or a completely custom amount.',
  },
  {
    id: 'faq-2',
    q: 'What happens after I make a reservation?',
    a: 'Once your payment is confirmed, your reservation becomes available for allocation. Our team reviews the available reservations, resource requirements, and priorities before deciding when work begins. Payment does not automatically guarantee an immediate start.',
  },
  {
    id: 'faq-3',
    q: 'Can I reserve more than once for the same request?',
    a: 'Yes. You can make multiple separate reservations toward the same application or AI tool, or toward specific components or resource categories. Each reservation is recorded separately with its own payment reference.',
  },
  {
    id: 'faq-4',
    q: 'Is BuildCredits affiliated with Rocket.new, Anthropic, or OpenAI?',
    a: 'Yes. BuildCredits is affiliated with Rocket.new. BuildCredits is built on and powered by the Rocket.new platform. References to Anthropic or OpenAI do not imply endorsement, partnership, or affiliation with those providers unless explicitly stated.',
  },
  {
    id: 'faq-5',
    q: 'How do I track progress?',
    a: 'Your customer dashboard shows real-time progress including milestones, customer-visible tasks, deliverables for review, and any questions requiring your input. You will also receive notifications when key events occur.',
  },
  {
    id: 'faq-6',
    q: 'What if I want changes after work has started?',
    a: 'You can submit a change request at any time. The team will review the impact on the architecture and, if additional resources are needed, provide a new recommended breakdown. As always, you decide whether and how much to reserve toward the change.',
  },
  {
    id: 'faq-7',
    q: 'Are my source code and business requirements kept confidential?',
    a: 'Yes. All requests, repositories and business requirements are treated as confidential. Only authorised BuildCredits team members assigned to your request will have access to your information.',
  },
];

export default function LandingFAQ() {
  const [openId, setOpenId] = useState<string | null>('faq-arch');

  return (
    <section id="faq" className="py-24 bg-muted/40">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="section-header text-primary mb-3">FAQ</p>
          <h2 className="text-hero-md font-extrabold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs?.map((faq) => (
            <div key={faq?.id} className="card overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-muted/40 transition-colors"
                onClick={() => setOpenId(openId === faq?.id ? null : faq?.id)}
                aria-expanded={openId === faq?.id}
              >
                <span className="text-sm font-semibold text-foreground pr-4">{faq?.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-muted-foreground shrink-0 transition-transform duration-200 ${
                    openId === faq?.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openId === faq?.id && (
                <div className="px-6 pb-5 animate-fade-in">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
